// ═══════════════════════════════════════════════════════════════
// BillingProvider.jsx — Billing context provider
// Wraps the app to provide billing state to all module components.
// ═══════════════════════════════════════════════════════════════

import React, { createContext, useContext, useMemo } from 'react';
import { getPlanLimits } from './BillingConfig.js';
import { getTrialStatus, getTenantDaysRemaining, isBillingReadOnly } from './BillingEnforcement.js';

const BillingContext = createContext(null);

/**
 * <BillingProvider
 *   user={{ id, name, email, role }}
 *   tenant={{ id, plan, status, ... }}
 *   billing={{ plan, planStatus, stripeCustomerId, ... }}
 *   stripeAdapter={adapter}
 *   isGrandfathered={boolean}
 * >
 *   {children}
 * </BillingProvider>
 */
function BillingProvider({ user, tenant, billing, stripeAdapter, isGrandfathered, children }) {
  const value = useMemo(() => ({
    user,
    tenant,
    billing,
    stripeAdapter,
    isGrandfathered: !!isGrandfathered,
    // Derived state
    planLimits: getPlanLimits(billing?.plan || tenant?.plan),
    trialStatus: getTrialStatus(billing, tenant),
    billingReadOnly: isBillingReadOnly(billing, tenant),
    daysRemaining: getTenantDaysRemaining(tenant),
  }), [user, tenant, billing, stripeAdapter, isGrandfathered]);

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

function useBilling() {
  const ctx = useContext(BillingContext);
  if (!ctx) {
    // Return safe defaults when used outside provider (backward compat)
    return {
      user: null, tenant: null, billing: null, stripeAdapter: null,
      isGrandfathered: false, planLimits: null, trialStatus: { inTrial: false, daysRemaining: 0, expired: false },
      billingReadOnly: { readOnly: false, reason: '', canExport: true }, daysRemaining: null,
    };
  }
  return ctx;
}

export { BillingProvider, useBilling, BillingContext };
